import { useState } from 'react'
import { PiGlobe } from 'react-icons/pi'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import QuickActionsModal from '@/components/dashboard/QuickActionsModal/QuickActionsModal'
import { SUPPORTED_LANGUAGES, Language, languageStorage } from '@/i18n'
import { useUserStore } from '@/store/useUserStore'

import usersAPI from '@/services/api/users'

interface LanguageSelectorProps {
  className?: string
  isOpen?: boolean
  setOpen?: (open: boolean) => void
  hideButton?: boolean
}

export const LanguageSelector = ({ className = '', isOpen: controlledIsOpen, setOpen: controlledSetOpen, hideButton = false }: LanguageSelectorProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const { i18n } = useTranslation()
  const user = useUserStore((state) => state.user)
  
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen
  const setIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const newValue = typeof value === 'function' ? value(isOpen) : value

    if (controlledSetOpen) {
      controlledSetOpen(newValue)
    } else {
      setInternalIsOpen(newValue)
    }
  }

  const languageEntries = Object.entries(SUPPORTED_LANGUAGES)
  const actions = languageEntries.map(([code, { nativeName, flag }], index) => {
    const isFirst = index === 0
    const isLast = index === languageEntries.length - 1
    const isSelected = i18n.language === code
    
    const classes = [
      isFirst && 'rounded-t-[1.625rem]',
      isLast && 'rounded-b-[1.625rem]',
      isSelected && 'bg-zinc-700'
    ].filter(Boolean).join(' ')
    
    return {
      icon: <span className="text-2xl">{flag}</span>,
      text: nativeName,
      onClick: async () => {
        languageStorage.set(code as Language)
        await i18n.changeLanguage(code as Language)

        if (user) {
          try {
            await usersAPI.update({ user: { language: code } })
          } catch { }
        }

        setIsOpen(false)
      },
      className: classes,
    }
  })

  return (
    <>
      {!hideButton && (
        <motion.button
          onClick={() => setIsOpen(true)}
          whileTap={{ scale: 0.92 }}
          className={`pl-3 -mt-[0.6rem] rounded-full hover:bg-zinc-100 transition-colors ${className}`}
          aria-label="Select language"
        >
          <PiGlobe className="h-6 w-6 text-zinc-700" />
        </motion.button>
      )}
      <QuickActionsModal
        actions={actions}
        isOpen={isOpen}
        setOpen={setIsOpen}
      />
    </>
  )
}

export default LanguageSelector
